import { isStripeEnabled } from "common";
import React, { FC, useMemo, useState } from "react";
import { RemoteGatewayType, Seller } from "types";
import { ContentLayout } from "../../Layout/ContentLayout";
import { AuthorizeNetGateway } from "./AuthorizeNetGateway";
import { BankAccount } from "./BankAccount";
import { CreditCardDescriptor } from "./CreditCardDescriptor";
import { CustomPaymentList } from "./CustomPaymentList";
import { GatewayOption, PaymentGatewaySelector } from "./PaymentGatewaySelector";
import StripeOAuthGateway from "./StripeOAuthGateway";
import { StripePaymentGateway } from "./StripePaymentGateway";
import { StripePaymentMethodSettings } from "./StripePaymentMethodSettings";
import { useSeller } from "@/hooks/useAccount";
import { hasStripeCustom, isStripeConnectOAuth, isStripeXola } from "@/models/Seller";

const gatewayComponents: Partial<Record<RemoteGatewayType, FC>> = {
    [RemoteGatewayType.STRIPE_XOLA]: StripePaymentGateway,
    [RemoteGatewayType.STRIPE_OAUTH]: StripeOAuthGateway,
    [RemoteGatewayType.AUTHORIZE_NET_CIM]: AuthorizeNetGateway,
};

export const PaymentSettingsTabs = () => {
    const seller = useSeller();
    const stripeEnabled = isStripeEnabled(seller);
    const showBankAccountInfo = isStripeXola(seller) && hasStripeCustom(seller);

    const [selected, setSelected] = useState<RemoteGatewayType | null>(getInitialGateway(seller));

    const handleChange = (option: GatewayOption) => {
        setSelected(option.value);
    };

    const GatewayComponent = useMemo(() => gatewayComponents[selected!] ?? StripePaymentGateway, [selected]);

    if (!selected) {
        return (
            <ContentLayout.Section title="Payment Gateway">
                <span>No Payment Gateway configured</span>
            </ContentLayout.Section>
        );
    }

    return (
        <ContentLayout isWide>
            <h2 className="mt-4">
                Payments
                <div className="text-base font-normal text-gray-darker">
                    Please provide information that is consistent with the information you provided on your most recent
                    tax fillings
                </div>
            </h2>
            <div className="divide-y divide-gray-lighter">
                <PaymentGatewaySelector selectedGateway={selected} onChange={handleChange} />
                <GatewayComponent />

                {stripeEnabled && showBankAccountInfo ? (
                    <ContentLayout.Section title="Bank Account">
                        <BankAccount />
                    </ContentLayout.Section>
                ) : null}

                <ContentLayout.Section title="Credit Card Settings">
                    <CreditCardDescriptor />
                </ContentLayout.Section>

                <ContentLayout.Section title="Payment Methods">
                    <CustomPaymentList />
                </ContentLayout.Section>

                {stripeEnabled && !isStripeConnectOAuth(seller.defaultGateway) ? <StripePaymentMethodSettings /> : null}
            </div>
        </ContentLayout>
    );
};

const getInitialGateway = (seller: Seller): RemoteGatewayType | null => {
    if (seller.defaultGateway?.id && seller.defaultGateway.type) {
        return seller.defaultGateway.type;
    }

    return seller.allowedGateways.length > 0 ? seller.allowedGateways[0] : null;
};
